import { AdminFormScheme } from "./scheme";
import { adminAPI } from "services/admin";
import { AxiosError } from "axios";
import { useAppStore } from "store/appStore";
import InputField from "components/fields/InputField";

import { FormProvider, useForm } from "react-hook-form";
import { Admin } from 'types/auth';

type Props = {
  admin?: Admin;
  admins: Admin[];
  onSuccess: () => void;
	handleOpenChange(): void;
}

export const Credentials :React.FC<Props> = ({
  admins,
  admin,
  onSuccess,
	handleOpenChange
}) => {
  const { admin: loggedInAdmin } = useAppStore();
  const selfUpdating = admin?.id === loggedInAdmin?.id;

  const form = useForm<AdminFormScheme>({
    values: {
      email: admin?.email || "",
      password: "",
      oldPassword: "",
    },
  });

  const handleSubmit = async (data: AdminFormScheme) => {
    try {
      if (!admin) {
        await adminAPI.createAdmin(data.email, data.password);
      } else {
        if (admins.some((el) => el.email === data.email && el.id !== admin.id))
          throw new Error("Admin already exists");

        if (selfUpdating) {
          await adminAPI.updateSelfPassword(data.oldPassword, data.password);

          return;
        }

        await adminAPI.updateAdmin(admin.id, data.password);
      }

      handleOpenChange();
      onSuccess();
    } catch (error) {
      if (error instanceof AxiosError) {
        form.setError("root", {
          type: "manual",
          message: error.response?.data.message || error.message,
        });
      } else if (error instanceof Error) {
        form.setError("root", {
          type: "manual",
          message: error.message,
        });
      }

      console.warn(error);
    }
  };
  return (
    <form onSubmit={form.handleSubmit(handleSubmit)}>
      <div className="max-h-[480px] grow overflow-y-auto overflow-x-hidden px-px">
        <FormProvider {...form}>
          <fieldset className="mb-[15px] flex flex-col">
            <InputField
              variant="auth"
              state={form.formState.errors.email && "error"}
              extra="w-full mb-3"
              label="Email:"
              placeholder="example@example.com"
              id="email"
              type="email"
              {...form.register("email", {
                required: "This field is required",
                pattern: {
                  value: /^[a-zA-Z0-9@.]+$/,
                  message: "Use only Latin letters",
                },
              })}
              disabled={!!admin}
            />
            {form.formState.errors.email && (
              <span className="text-sm text-red-500 dark:text-red-400">
                {form.formState.errors.email.message}
              </span>
            )}
          </fieldset>

          {selfUpdating && (
            <fieldset className="mb-[15px] flex flex-col">
              <InputField
                variant="auth"
                state={form.formState.errors.oldPassword && "error"}
                extra="w-full mb-3"
                label="Old password:"
                placeholder="password"
                id="oldPassword"
                type="password"
                {...form.register("oldPassword", {
                  required: "This field is required",
                  minLength: 6,
                  maxLength: 12,
                })}
              />
              {form.formState.errors.oldPassword && (
                <span className="text-sm text-red-500 dark:text-red-400">
                  {form.formState.errors.oldPassword.message}
                </span>
              )}
            </fieldset>
          )}

          <fieldset className="mb-[15px] flex flex-col">
            <InputField
              variant="auth"
              state={form.formState.errors.password && "error"}
              extra="w-full mb-3"
              label={
                admin
                  ? "New password (min: 6 symbols, max: 12 symbols):"
                  : "Password (min: 6 symbols, max: 12 symbols):"
              }
              placeholder="password"
              id="password"
              type="password"
              {...form.register("password", {
                required: "This field is required",
                minLength: 6,
                maxLength: 12,
                pattern: {
                  value: /^[^\u0400-\u04FF]+$/,
                  message: "Cyrillic characters are not allowed",
                },
              })}
            />
            {form.formState.errors.password && (
              <span className="text-sm text-red-500 dark:text-red-400">
                {form.formState.errors.password.message}
              </span>
            )}
          </fieldset>
        </FormProvider>
      </div>
      <div className="mt-[10px] flex flex-col">
        {form.formState.errors.root && (
          <span className="text-sm capitalize text-red-500 dark:text-red-400">
            {form.formState.errors.root.message}
          </span>
        )}
        <button
          type="submit"
          className="linear mt-2 w-full rounded-xl bg-brand-500
				py-[12px] text-base font-medium text-white transition
				duration-200 hover:bg-brand-600 active:bg-brand-700
				disabled:pointer-events-none disabled:opacity-50 dark:bg-brand-400
				dark:text-white dark:hover:bg-brand-300 dark:active:bg-brand-200"
          disabled={
            form.formState.isSubmitting ||
            !form.formState.isDirty ||
            !form.formState.isValid
          }
        >
          {form.formState.isSubmitting ? "Saving..." : "Save changes"}
        </button>
      </div>
    </form>
  );
};
